import {
  CheckCircleIcon,
  InfoIcon,
  WarningCircleIcon,
  WarningIcon,
  XIcon,
} from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { Button } from './Button';
import { cn } from './lib/cn';

/* Named for meaning, like `BadgeTone` and `ToastTone`. */
export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

export type AlertProps = {
  tone?: AlertTone;
  /** Bold first line. Omit for a one-line notice. */
  title?: ReactNode;
  /** The message itself, under the title. */
  children?: ReactNode;
  /** Sits under the message — typically a small Button or a link. */
  action?: ReactNode;
  /** Shows a dismiss button; the caller decides whether the alert goes away. */
  onDismiss?: () => void;
  className?: string;
};

/* Same token pairs as `Badge`, so a screen's alert and its badges agree. */
const TONES: Record<AlertTone, string> = {
  info: 'sk:bg-info-bg',
  success: 'sk:bg-success-bg',
  warning: 'sk:bg-warning-bg',
  danger: 'sk:bg-danger-bg',
};

const ICON_TONES: Record<AlertTone, string> = {
  info: 'sk:text-info',
  success: 'sk:text-success',
  warning: 'sk:text-warning',
  danger: 'sk:text-danger',
};

const ICONS: Record<AlertTone, ReactNode> = {
  info: <InfoIcon size={18} weight="fill" />,
  success: <CheckCircleIcon size={18} weight="fill" />,
  warning: <WarningIcon size={18} weight="fill" />,
  danger: <WarningCircleIcon size={18} weight="fill" />,
};

/*
 * `alert` is assertive and interrupts the screen reader, so it is kept for
 * what the user must act on; good news and background notes wait their turn.
 */
const ROLES: Record<AlertTone, 'alert' | 'status'> = {
  info: 'status',
  success: 'status',
  warning: 'alert',
  danger: 'alert',
};

/**
 * An inline callout inside the page — a banner above a form, a warning in a
 * card. Unlike the toast it stays where it is put until the page removes it.
 */
export function Alert({
  tone = 'info',
  title,
  children,
  action,
  onDismiss,
  className,
}: AlertProps) {
  return (
    <div
      role={ROLES[tone]}
      data-tone={tone}
      className={cn(
        'sk:flex sk:w-full sk:min-w-0 sk:items-start sk:gap-2.5 sk:rounded-card sk:p-3',
        'sk:inset-shadow-hairline sk:font-sans',
        TONES[tone],
        className,
      )}
    >
      <span
        aria-hidden
        className={cn('sk:mt-px sk:flex sk:shrink-0', ICON_TONES[tone])}
      >
        {ICONS[tone]}
      </span>

      <div className="sk:flex sk:min-w-0 sk:flex-1 sk:flex-col sk:gap-0.5">
        {title ? (
          <div className="sk:text-sm sk:font-medium sk:text-fg sk:break-words">
            {title}
          </div>
        ) : null}
        {children ? (
          <div className="sk:text-dense sk:tracking-dense sk:text-fg-secondary sk:break-words">
            {children}
          </div>
        ) : null}
        {action ? <div className="sk:mt-2 sk:flex">{action}</div> : null}
      </div>

      {onDismiss ? (
        <Button
          variant="ghost"
          size="sm"
          shape="circle"
          aria-label="Dismiss"
          className="sk:shrink-0"
          onPress={onDismiss}
        >
          <XIcon size={12} weight="bold" aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}
